import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { countries } from 'shared/data/siteSettings';
import Button from 'components/commercetools-ui/atoms/button';
import Modal from 'components/commercetools-ui/atoms/modal';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { sortCountries } from '../helpers/sortCountries';

interface MarketModalProps {
  showMarket?: boolean;
  hideMarketMenu?: () => void;
  selectedCountry?: string;
  selectedCurrency?: string;
  handleCountryChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  handleCurrencyChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  handleMarketClick?: () => void;
  currencyOptions: { currency: string; currencyCode: string }[];
  isLoading?: boolean;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

const MarketModal = ({
  showMarket = false,
  hideMarketMenu,
  selectedCountry,
  selectedCurrency,
  handleCountryChange,
  handleCurrencyChange,
  handleMarketClick,
  currencyOptions,
  isLoading,
  setIsLoading,
}: MarketModalProps) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const sortedCountries = sortCountries(countries);

  //country selector is rendered multiple time. Cache the response and show the delivery countries returned by the serverd
  //const { siteSelectorCountries } = useConfig();
  //siteSelectorCountries().then((response) => console.log(response));

  return (
    <Modal isOpen={showMarket} className="relative bg-neutral-200">
      <div className="flex flex-col items-end px-16 pt-20">
        <Button
          variant="ghost"
          onClick={hideMarketMenu}
          title={formatMessage({ id: 'close', defaultMessage: 'Close' })}
          size="xs"
          className="group flex gap-4 rounded-md px-16 hover:bg-btnPrimary-active hover:text-white"
        >
          <span>Close</span>
          <XMarkIcon className="w-14 font-medium text-red-500 group-hover:text-inherit" />
        </Button>
      </div>
      <div className="flex flex-col items-center px-16 pt-20">
        <Typography as="h5" className="w-4/5 text-center text-24 font-bold text-primary-black md:w-3/5 md:text-32">
          {'Please select your shipping country & currency'}
        </Typography>
      </div>
      <div className="flex flex-col items-start justify-center px-16 pt-20 md:flex-row md:items-center">
        <Typography className="mb-4 w-100">Country</Typography>
        <select className="w-full rounded-sm md:w-300" value={selectedCountry} onChange={handleCountryChange}>
          {sortedCountries?.map((country) => (
            <option value={country?.countryCode} key={country?.countryCode}>{`${country?.country}`}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col items-start justify-center px-16 pt-20 md:flex-row md:items-center">
        <Typography className="mb-4 w-100">Currency</Typography>
        <select className="w-full rounded-sm md:w-300" value={selectedCurrency} onChange={handleCurrencyChange}>
          {currencyOptions?.map((option: { currency: string; currencyCode: string }) => (
            <option key={option.currency} value={option.currency}>
              {option.currency} ({option.currencyCode})
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-center px-16 pb-80 pt-20">
        <Button
          loading={isLoading}
          className="w-full md:w-200"
          onClick={() => {
            if (isLoading) return;
            setIsLoading && setIsLoading(true);

            setTimeout(() => {
              handleMarketClick?.();
            }, 10);
          }}
        >
          Proceed
        </Button>
      </div>
    </Modal>
  );
};

export default MarketModal;
